import { useState } from 'react'
import { BookOpen, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react'
import api from '../lib/api'
import { PROFILES } from '../utils/sectionProfiles'

// ── Temporal status config ────────────────────────────────────────────────────

const TEMPORAL = {
  FOUNDATIONAL: { color: '#1e40af', bg: '#dbeafe', label: 'Foundational', note: 'Enduring anchors — cite repeatedly, acknowledge even when critiqued' },
  ESTABLISHED:  { color: '#059669', bg: '#d1fae5', label: 'Established',  note: 'Settled consensus — cite with confidence' },
  EMERGING:     { color: '#d97706', bg: '#fef3c7', label: 'Emerging',     note: 'Provisional — frame cautiously; use hedged language' },
  CONTESTED:    { color: '#dc2626', bg: '#fee2e2', label: 'Contested',    note: 'Live debate — let these generate interpretive tension' },
  DECLINING:    { color: '#94a3b8', bg: '#f1f5f9', label: 'Aging',        note: 'Trace historical lineage; do not let these dominate' },
}

const TEMPORAL_ORDER = ['FOUNDATIONAL', 'ESTABLISHED', 'EMERGING', 'CONTESTED', 'DECLINING']

// ── Collapsible block ─────────────────────────────────────────────────────────

function Block({ title, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div style={{ marginBottom: '0.875rem' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: 'none', border: 'none', cursor: 'pointer',
          padding: '4px 0', color: '#6b7280',
        }}
      >
        <span style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {title}
        </span>
        {open ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
      </button>
      {open && <div style={{ paddingTop: 6 }}>{children}</div>}
    </div>
  )
}

// ── Temporal distribution strip ───────────────────────────────────────────────

function TemporalStrip({ temporal, total }) {
  if (!temporal || !total) return null

  return (
    <div style={{ marginBottom: 12 }}>
      {/* Stacked bar */}
      <div style={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', marginBottom: 8 }}>
        {TEMPORAL_ORDER.map(key => {
          const cfg   = TEMPORAL[key]
          const count = temporal[key]?.count || 0
          const pct   = (count / total) * 100
          if (!pct) return null
          return (
            <div
              key={key}
              title={`${cfg.label}: ${count}`}
              style={{ width: `${pct}%`, background: cfg.color, transition: 'width 0.4s' }}
            />
          )
        })}
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 12px' }}>
        {TEMPORAL_ORDER.map(key => {
          const cfg   = TEMPORAL[key]
          const count = temporal[key]?.count || 0
          if (!count) return null
          return (
            <span key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: cfg.color, flexShrink: 0 }} />
              <span style={{ fontSize: '0.65rem', color: '#374151' }}>
                <strong>{cfg.label}</strong> {count}
              </span>
            </span>
          )
        })}
      </div>
    </div>
  )
}

// ── Temporal rows (detailed) ──────────────────────────────────────────────────

function TemporalRow({ statusKey, data }) {
  const cfg = TEMPORAL[statusKey]
  if (!data?.count) return null
  return (
    <div style={{
      marginBottom: 8, padding: '0.5rem 0.625rem',
      background: cfg.bg, borderLeft: `3px solid ${cfg.color}`,
      borderRadius: '0 4px 4px 0',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
        <span style={{
          fontSize: '0.65rem', fontWeight: 700, color: cfg.color,
          textTransform: 'uppercase', letterSpacing: '0.05em',
        }}>
          {cfg.label} · {data.count}
        </span>
      </div>
      {data.authors?.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 3 }}>
          {data.authors.map(a => (
            <span key={a} style={{
              fontSize: '0.65rem', padding: '1px 6px',
              background: '#fff', color: cfg.color,
              border: `1px solid ${cfg.color}40`,
              borderRadius: 10,
            }}>{a}</span>
          ))}
        </div>
      )}
      <p style={{ fontSize: '0.68rem', color: '#6b7280', margin: 0, fontStyle: 'italic' }}>
        {cfg.note}
      </p>
    </div>
  )
}

// ── Citation density map (static) ─────────────────────────────────────────────

function DensityRow({ profile }) {
  const pct   = Math.round(profile.citationDensityLevel * 100)
  const color = profile.citationDensityLevel >= 0.75 ? '#7c3aed'
    : profile.citationDensityLevel >= 0.50           ? '#2563eb'
    : profile.citationDensityLevel >= 0.25           ? '#d97706'
    : '#94a3b8'

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: profile.color, flexShrink: 0 }} />
      <span style={{ fontSize: '0.72rem', color: '#374151', width: 130, flexShrink: 0 }}>{profile.label}</span>
      <div style={{ flex: 1, height: 4, borderRadius: 2, background: '#e5e7eb', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 2, transition: 'width 0.4s' }} />
      </div>
      <span style={{ fontSize: '0.6rem', fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.05em', width: 68, textAlign: 'right', flexShrink: 0 }}>
        {profile.citationDensityLabel}
      </span>
    </div>
  )
}

// ── Root component ────────────────────────────────────────────────────────────

export default function LiteratureEcologyPanel({ thesisId }) {
  const [state,  setState]  = useState('idle')
  const [data,   setData]   = useState(null)
  const [open,   setOpen]   = useState(false)

  async function load() {
    setState('loading')
    try {
      const { data: result } = await api.get(`/thesis/${thesisId}/literature-ecology`)
      setData(result)
      setState('done')
      setOpen(true)
    } catch {
      setState('error')
      setOpen(true)
    }
  }

  const densityOrder = [
    'literature_review', 'discussion', 'introduction',
    'methodology', 'findings', 'conclusion', 'limitations', 'appendix',
  ]

  return (
    <div>
      {/* Trigger row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '0.75rem' }}>
        <button
          onClick={state === 'idle' ? load : () => setOpen(o => !o)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '0.5rem 0.875rem',
            background: '#1e3a5f', color: '#93c5fd',
            border: '1px solid rgba(147,197,253,0.3)', borderRadius: 8,
            fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
          }}
        >
          {state === 'loading'
            ? <RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} />
            : <BookOpen size={13} />
          }
          {state === 'idle' ? 'Open Literature Ecology' : open ? 'Hide Ecology' : 'Show Ecology'}
        </button>

        {state === 'done' && (
          <button onClick={load} title="Refresh" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: 4 }}>
            <RefreshCw size={12} />
          </button>
        )}
      </div>

      {state === 'error' && (
        <p style={{ fontSize: '0.78rem', color: '#dc2626' }}>
          Add references to your thesis to enable ecology analysis.
        </p>
      )}

      {open && (state === 'done' || state === 'idle') && (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', overflow: 'hidden', padding: '0.875rem' }}>

          {/* ── Temporal balance ─────────────────────────────────── */}
          {data?.temporal && (
            <Block title={`Literature Maturity — ${data.totalRefs} references`} defaultOpen={true}>
              <TemporalStrip temporal={data.temporal} total={data.totalRefs} />
              {TEMPORAL_ORDER.map(key => (
                <TemporalRow key={key} statusKey={key} data={data.temporal[key]} />
              ))}
            </Block>
          )}

          {/* ── Gravity authors ──────────────────────────────────── */}
          {data?.gravityAuthors?.length > 0 && (
            <Block title="Foundational Author Map" defaultOpen={true}>
              <p style={{ fontSize: '0.7rem', color: '#9ca3af', marginBottom: 8 }}>
                Authors appearing in multiple references — these carry citation gravity and recur naturally across chapters.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {data.gravityAuthors.map(a => {
                  const isFoundational = data.temporal?.FOUNDATIONAL?.authors?.includes(a)
                  return (
                    <span key={a} style={{
                      fontSize: '0.72rem', fontWeight: 600,
                      padding: '2px 9px', borderRadius: 12,
                      background: isFoundational ? '#dbeafe' : '#f1f5f9',
                      color: isFoundational ? '#1e40af' : '#374151',
                      border: isFoundational ? '1px solid #93c5fd' : '1px solid #e2e8f0',
                    }}>
                      {a}{isFoundational ? ' ★' : ''}
                    </span>
                  )
                })}
              </div>
            </Block>
          )}

          {/* ── Dominant journals ────────────────────────────────── */}
          {data?.dominantJournals?.length > 0 && (
            <Block title="Recurring Journals" defaultOpen={false}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {data.dominantJournals.map(j => (
                  <div key={j} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#6366f1', flexShrink: 0 }} />
                    <span style={{ fontSize: '0.75rem', color: '#374151' }}>{j}</span>
                  </div>
                ))}
              </div>
            </Block>
          )}

          {/* ── Citation density map (always-on) ─────────────────── */}
          <Block title="Citation Density by Section" defaultOpen={!data}>
            <p style={{ fontSize: '0.7rem', color: '#9ca3af', marginBottom: 10 }}>
              Expected citation intensity per section type.
            </p>
            {densityOrder.map(id => <DensityRow key={id} profile={PROFILES[id]} />)}
          </Block>

        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}
